/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { SalesDashboardChartTooltip } from "./SalesDashboardChartTooltip";
import { commonStyles } from "./chartHelper";
import {
  createUserAttributeDashboardData,
  genderChartSeries,
} from "./userAttributeDashboardData";

import type { GetUserAttributeCountsResponse } from "./useGetUserAttributeCounts";

export const UserAttributeDashboard: React.FC<{
  data: GetUserAttributeCountsResponse | undefined;
  error: unknown;
  isLoading: boolean;
}> = ({ data, error, isLoading }) => {
  if (!data) {
    if (isLoading) {
      return <UserAttributeDashboardSkeleton />;
    }

    return (
      <UserAttributeDashboardState
        hasError={error !== undefined}
        message={
          error === undefined
            ? // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
              "データがありません"
            : // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
              "購入者属性データの取得に失敗しました"
        }
      />
    );
  }

  if (!hasUserAttributeCounts(data)) {
    return (
      <UserAttributeDashboardState
        hasError={false}
        // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        message="データがありません"
      />
    );
  }

  return <UserAttributeCharts data={data} />;
};

const UserAttributeCharts: React.FC<{
  data: GetUserAttributeCountsResponse;
}> = ({ data }) => {
  const { ageGroupChartData, genderChartData, prefectureChartData } =
    React.useMemo(() => createUserAttributeDashboardData(data), [data]);
  const genderTotal = genderChartData.reduce(
    (total, { count }) => total + count,
    0
  );
  const prefectureTotal = prefectureChartData.reduce(
    (total, { count }) => total + count,
    0
  );
  const prefectureMaxCount = Math.max(
    0,
    ...prefectureChartData.map(({ count }) => count)
  );

  return (
    <div css={styles.dashboard}>
      <div css={styles.topCharts}>
        <section css={styles.card}>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <h4 css={styles.cardTitle}>年齢別 購入者数</h4>
          <ul css={styles.ageGroupLegend}>
            {genderChartSeries.map(({ gender, label, color }) => (
              <li css={styles.legendItem} key={gender}>
                <span css={styles.squareLegendMarker(color)} />
                <span>{label}</span>
              </li>
            ))}
          </ul>

          <div css={styles.ageGroupChartScroll}>
            <div css={styles.ageGroupChart}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  barCategoryGap={12}
                  data={ageGroupChartData}
                  margin={ageGroupChartMargin}
                >
                  <CartesianGrid stroke="#2e3d4c" vertical={false} />
                  <XAxis
                    axisLine={{ stroke: "#8b96a2" }}
                    dataKey="label"
                    interval={0}
                    tick={{ fill: "#8b96a2", fontSize: 11 }}
                    tickLine={false}
                    tickMargin={8}
                  />
                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    label={{
                      angle: -90,
                      fill: "#8b96a2",
                      fontSize: 12,
                      position: "left",
                      // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
                      value: "購入者数",
                    }}
                    tick={{ fill: "#8b96a2", fontSize: 11 }}
                    tickLine={false}
                    width={46}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(31, 41, 55, 0.04)" }}
                    content={
                      <SalesDashboardChartTooltip
                        formatValue={formatUserCount}
                        getSeriesColor={getGenderSeriesColor}
                      />
                    }
                  />
                  {genderChartSeries.map(
                    ({ gender, key, label, color }, index) => (
                      <Bar
                        dataKey={key}
                        fill={color}
                        key={gender}
                        maxBarSize={34}
                        name={label}
                        stackId="ageGroup"
                      >
                        {index === genderChartSeries.length - 1 && (
                          <LabelList
                            dataKey="total"
                            fill="#b9c3cd"
                            fontSize={11.5}
                            fontWeight={700}
                            position="top"
                          />
                        )}
                      </Bar>
                    )
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section css={styles.card}>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <h4 css={styles.cardTitle}>性別 購入者数</h4>
          <div css={styles.genderChart}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={genderChartData}
                  dataKey="count"
                  innerRadius={57}
                  nameKey="label"
                  outerRadius={90}
                  paddingAngle={1.4}
                  stroke="none"
                >
                  {genderChartData.map(({ gender, color }) => (
                    <Cell fill={color} key={gender} />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <SalesDashboardChartTooltip
                      formatValue={formatUserCount}
                      getSeriesColor={getGenderSeriesColor}
                      // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
                      title="性別"
                    />
                  }
                  wrapperStyle={genderTooltipWrapperStyle}
                />
              </PieChart>
            </ResponsiveContainer>
            <div css={styles.genderChartTotal}>
              <span css={styles.genderChartTotalValue}>
                {genderTotal.toLocaleString("ja-JP")}
              </span>
              {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
              <span css={styles.genderChartTotalLabel}>合計（人）</span>
            </div>
          </div>

          <ul css={styles.genderLegend}>
            {genderChartData.map(({ gender, label, color, count }) => (
              <li css={styles.genderLegendItem} key={gender}>
                <span css={styles.circleLegendMarker(color)} />
                <span css={styles.genderLegendLabel}>{label}</span>
                <span css={styles.genderLegendCount}>
                  {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
                  {count.toLocaleString("ja-JP")}人
                </span>
                <span css={styles.genderLegendPercentage}>
                  {formatPercentage(count, genderTotal)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section css={styles.card}>
        <div css={styles.prefectureHeader}>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <h4 css={styles.cardTitle}>都道府県別 購入者数</h4>
          {/* eslint-disable no-restricted-syntax -- i18n 未対応 */}
          <span css={styles.prefectureTotal}>
            合計 {prefectureTotal.toLocaleString("ja-JP")}人
          </span>
          {/* eslint-enable no-restricted-syntax */}
        </div>

        <div css={styles.prefectureList}>
          {prefectureChartData.map(
            ({ prefecture, label, count, isNoAnswer }, index) => (
              <div css={styles.prefectureRow} key={prefecture}>
                <span css={styles.prefectureLabel}>{label}</span>
                <div css={styles.prefectureBarTrack}>
                  <div
                    css={styles.prefectureBar(
                      count,
                      prefectureMaxCount,
                      isNoAnswer,
                      index
                    )}
                  />
                </div>
                <span css={styles.prefectureCount(index)}>
                  {count.toLocaleString("ja-JP")}
                </span>
              </div>
            )
          )}
        </div>
      </section>
    </div>
  );
};

const UserAttributeDashboardSkeleton: React.FC = () => (
  <div
    css={styles.dashboard}
    aria-busy="true"
    // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
    aria-label="購入者属性を読み込み中"
  >
    <div css={styles.topCharts}>
      <section css={[styles.card, styles.topChartSkeletonCard]}>
        <div css={commonStyles.skeleton} />
      </section>
      <section css={[styles.card, styles.topChartSkeletonCard]}>
        <div css={commonStyles.skeleton} />
      </section>
    </div>
    <section css={[styles.card, styles.prefectureSkeletonCard]}>
      <div css={commonStyles.skeleton} />
    </section>
  </div>
);

const UserAttributeDashboardState: React.FC<{
  hasError: boolean;
  message: string;
}> = ({ hasError, message }) => (
  <div css={styles.statePanel} role={hasError ? "alert" : undefined}>
    {message}
  </div>
);

const hasUserAttributeCounts = ({
  ageGroupGenderCounts,
  genderCounts,
  prefectureCounts,
}: GetUserAttributeCountsResponse): boolean =>
  ageGroupGenderCounts.some(({ count }) => count > 0) ||
  genderCounts.some(({ count }) => count > 0) ||
  prefectureCounts.some(({ count }) => count > 0);

const getGenderSeriesColor = (seriesName: string): string | undefined =>
  genderChartSeries.find(({ label }) => label === seriesName)?.color;

const formatUserCount = (value: number): string =>
  `${value.toLocaleString("ja-JP")}人`;

const formatPercentage = (count: number, total: number): string => {
  return total === 0 ? "0.00%" : `${((count / total) * 100).toFixed(2)}%`;
};

const ageGroupChartMargin = {
  bottom: 4,
  left: 10,
  right: 18,
  top: 22,
};

const genderTooltipWrapperStyle = {
  zIndex: 1,
};

const styles = {
  dashboard: css`
    display: flex;
    flex-direction: column;
    gap: 14px;
  `,
  topCharts: css`
    display: grid;
    grid-template-columns: repeat(
      auto-fit,
      minmax(min(100%, calc(340 / 16 * 1rem)), 1fr)
    );
    gap: 14px;
  `,
  card: css`
    min-width: 0;
    padding: clamp(16px, 2.5vw, 24px);
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.22);
  `,
  topChartSkeletonCard: css`
    height: 376px;
  `,
  prefectureSkeletonCard: css`
    height: 220px;
  `,
  statePanel: css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 220px;
    padding: 24px;
    color: #b9c3cd;
    font-size: 14px;
    text-align: center;
    background: #1b2734;
    border: 1px solid #2e3d4c;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.22);
  `,
  cardTitle: css`
    margin: 0;
    color: #ffffff;
    font-size: 14.5px;
    font-weight: 700;
    letter-spacing: 0.4px;
  `,
  ageGroupLegend: css`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    padding: 0;
    margin: 10px 0 8px;
    list-style: none;
  `,
  legendItem: css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #b9c3cd;
    font-size: 12.5px;
  `,
  squareLegendMarker: (color: string) => css`
    width: 9px;
    height: 9px;
    background: ${color};
    border-radius: 2px;
  `,
  ageGroupChartScroll: css`
    width: 100%;
    overflow-x: auto;
  `,
  ageGroupChart: css`
    width: 100%;
    min-width: 560px;
    height: 302px;
  `,
  genderChart: css`
    position: relative;
    width: 100%;
    height: 196px;
    margin: 0 auto;
  `,
  genderChartTotal: css`
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: none;
    transform: translate(-50%, -50%);
  `,
  genderChartTotalValue: css`
    color: #ffffff;
    font-size: 32px;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  `,
  genderChartTotalLabel: css`
    margin-top: 8px;
    color: #8b96a2;
    font-size: 13px;
  `,
  genderLegend: css`
    display: flex;
    flex-direction: column;
    gap: 9px;
    padding: 0;
    margin: 14px 0 0;
    list-style: none;
  `,
  genderLegendItem: css`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13.5px;
  `,
  circleLegendMarker: (color: string) => css`
    flex: none;
    width: 10px;
    height: 10px;
    background: ${color};
    border-radius: 50%;
  `,
  genderLegendLabel: css`
    flex: 1;
    color: #b9c3cd;
    font-weight: 700;
  `,
  genderLegendCount: css`
    color: #ffffff;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  `,
  genderLegendPercentage: css`
    width: 56px;
    color: #8b96a2;
    font-size: 12.5px;
    text-align: right;
    font-variant-numeric: tabular-nums;
  `,
  prefectureHeader: css`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 14px;
  `,
  prefectureTotal: css`
    color: #8b96a2;
    font-size: 12.5px;
    letter-spacing: 0.3px;
    white-space: nowrap;
  `,
  prefectureList: css`
    column-width: 300px;
    column-gap: 48px;
  `,
  prefectureRow: css`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 3.5px 0;
    break-inside: avoid;
  `,
  prefectureLabel: css`
    flex: none;
    width: 56px;
    color: #b9c3cd;
    font-size: 12px;
    text-align: right;
  `,
  prefectureBarTrack: css`
    flex: 1;
    height: 13px;
    overflow: hidden;
    background: #243342;
    border-radius: 3px;
  `,
  prefectureBar: (
    count: number,
    maxCount: number,
    isNoAnswer: boolean,
    index: number
  ) => css`
    width: ${maxCount === 0 ? 0 : (count / maxCount) * 100}%;
    height: 100%;
    background: ${isNoAnswer ? "#8b96a2" : "#00c2ae"};
    border-radius: 3px;
    opacity: ${index > 2 && !isNoAnswer ? 0.75 : 1};
  `,
  prefectureCount: (index: number) => css`
    flex: none;
    width: 34px;
    color: ${index < 3 ? "#ffffff" : "#8b96a2"};
    font-size: 11.5px;
    font-weight: ${index < 3 ? 700 : 400};
    text-align: right;
    font-variant-numeric: tabular-nums;
  `,
};
